import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, animate } from "framer-motion";
import { ArrowRight, ShieldCheck, Globe2, Sparkles, ChevronRight } from "lucide-react";
import { SEO } from "@/components/SEO";
import { RevealText } from "@/components/shared/RevealText";
import { Marquee } from "@/components/shared/Marquee";
import { SectionReveal, StaggerGroup, StaggerItem } from "@/components/shared/SectionReveal";
import { AnimatedCounter } from "@/components/shared/AnimatedCounter";
import { STORE_LINKS } from "@/data/links";
import { USE_CASE_SLUGS, USE_CASE_IMAGES, PHONE_SCREENS, IMAGES } from "@/data/images";
import apiClient from "@/lib/api";
import { useEffect, useState } from "react";
import { DownloadButton } from "@/components/shared/DownloadButton";

const ICONS = [ShieldCheck, Globe2, Sparkles];

export default function Home() {
  const { t } = useTranslation();
  const { lang = "fr" } = useParams();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const mockupY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const mockupRotate = useTransform(scrollYProgress, [0, 1], [9, 3]);
  const [stats, setStats] = useState({ users_protected: 48200, alerts_sent: 132600, safe_places: 9400 });
  const [screenIdx, setScreenIdx] = useState(0);
  const rotateY = useMotionValue(0);

  useEffect(() => {
    apiClient.get("/stats").then((res) => setStats(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    let pastHalf = false;
    const controls = animate(rotateY, 360, {
      duration: 6,
      repeat: Infinity,
      ease: "linear",
      onUpdate: (latest) => {
        const isPastHalf = latest % 360 > 180;
        if (isPastHalf && !pastHalf) {
          setScreenIdx((i) => (i + 1) % PHONE_SCREENS.length);
        }
        pastHalf = isPastHalf;
      },
    });
    return controls.stop;
  }, [rotateY]);

  const uspItems = t("home.usp.items", { returnObjects: true });
  const useCaseItems = t("use_cases.items", { returnObjects: true });

  const schema = {
    "@context": "https://schema.org",
    "@type": "MobileApplication",
    name: "Fastority",
    applicationCategory: "SecurityApplication",
    operatingSystem: "iOS, Android",
    offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
    downloadUrl: [STORE_LINKS.ios, STORE_LINKS.android],
  };

  return (
    <div data-testid="home-page">
      <SEO title={t("meta.default_title")} description={t("meta.default_description")} schema={schema} />

      <section ref={heroRef} className="relative bg-deepblue text-white overflow-hidden grain-overlay">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,90,95,0.18),transparent_55%)]" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-24 sm:pt-24 sm:pb-32 grid lg:grid-cols-2 gap-16 items-center relative">
          <div>
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] bg-white/10 border border-white/15 rounded-full px-4 py-2 mb-8"
              data-testid="hero-eyebrow"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emeraldgreen" />
              {t("home.hero.eyebrow")}
            </motion.span>

            <h1 className="font-heading font-extrabold text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[1.05]" data-testid="hero-title">
              <RevealText lines={[t("home.hero.title_line1")]} />
              <RevealText lines={[t("home.hero.title_line2")]} className="text-coral" delay={0.15} />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.4 }}
              className="mt-8 text-lg text-white/70 max-w-md leading-relaxed"
              data-testid="hero-subtitle"
            >
              {t("home.hero.subtitle")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.22, duration: 0.4 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <DownloadButton testId="hero-cta-primary" className="inline-flex items-center gap-2 bg-coral text-white font-semibold px-7 py-4 rounded-full hover:scale-[1.02] transition-transform duration-300">
                {t("home.hero.cta_primary")} <ArrowRight size={18} />
              </DownloadButton>
              <Link to={`/${lang}/features`} data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 text-white/80 font-medium px-6 py-4 rounded-full border border-white/20 hover:bg-white/10 transition-colors">
                {t("home.hero.cta_secondary")} <ChevronRight size={16} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.35 }}
              className="mt-10 flex items-center gap-4"
            >
              <a href={STORE_LINKS.ios} target="_blank" rel="noreferrer" data-testid="hero-appstore-badge" className="text-xs font-semibold text-white/60 border border-white/15 rounded-lg px-3 py-2 hover:text-white hover:border-white/40 transition-colors">
                {t("common.download_ios")}
              </a>
              <a href={STORE_LINKS.android} target="_blank" rel="noreferrer" data-testid="hero-googleplay-badge" className="text-xs font-semibold text-white/60 border border-white/15 rounded-lg px-3 py-2 hover:text-white hover:border-white/40 transition-colors">
                {t("common.download_android")}
              </a>
            </motion.div>
          </div>

          <motion.div
            style={{ y: mockupY }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.25, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto"
          >
            <div className="absolute inset-0 blur-3xl bg-coral/20 rounded-full scale-75 -z-10" />
            <div className="relative w-[280px] sm:w-[320px] aspect-[4/5] rounded-[2.5rem] bg-gradient-to-br from-[#3450b8] via-deepblue to-[#101d4d] shadow-2xl flex items-center justify-center p-6 overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_28%_18%,rgba(255,255,255,0.12),transparent_60%)]" />
              <motion.div style={{ rotate: mockupRotate, perspective: "2600px" }} className="relative w-[160px] sm:w-[185px] aspect-[884/1798] mx-auto" data-testid="hero-app-mockup-wrapper">
                <motion.div style={{ rotateY, transformStyle: "preserve-3d" }} className="relative w-full h-full" data-testid="hero-app-mockup-spin">
                  <img
                    src={PHONE_SCREENS[screenIdx]}
                    alt="Fastority - écran de l'application"
                    style={{ backfaceVisibility: "hidden", transform: "translateZ(7px)" }}
                    className="absolute inset-0 w-full h-full object-cover rounded-[2.2rem] drop-shadow-2xl"
                    data-testid="hero-app-mockup"
                  />
                  <div
                    style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg) translateZ(7px)" }}
                    className="absolute inset-0 rounded-[2.2rem] bg-gradient-to-br from-slate-700 via-slate-900 to-black shadow-2xl flex items-center justify-center"
                    data-testid="hero-app-mockup-back"
                  >
                    <img src={IMAGES.logo} alt="Fastority" className="w-14 h-14 object-contain opacity-90 rounded-2xl" />
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <Marquee text={t("home.marquee")} className="bg-slate900 text-white" />

      <section className="py-24 sm:py-32 bg-offwhite">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionReveal className="max-w-2xl mb-16">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-coral mb-3 block">Fastority</span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate900">
              {t("home.usp.title")}
            </h2>
            <p className="mt-4 text-lg text-slate-600">{t("home.usp.subtitle")}</p>
          </SectionReveal>

          <StaggerGroup className="grid sm:grid-cols-3 gap-6">
            {uspItems.map((item, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <StaggerItem key={item.title}>
                  <div className="bg-white rounded-3xl p-8 h-full shadow-[0_8px_30px_rgba(30,58,138,0.08)] border border-gray-100 hover:-translate-y-1 transition-transform duration-300">
                    <div className="w-12 h-12 rounded-2xl bg-deepblue/10 flex items-center justify-center mb-6">
                      <Icon className="text-deepblue" size={22} strokeWidth={2.2} />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-slate900 mb-2">{item.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      <section className="py-24 bg-deepblue text-white relative overflow-hidden grain-overlay">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionReveal className="text-center mb-14">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">{t("home.stats.title")}</h2>
          </SectionReveal>
          <div className="grid sm:grid-cols-3 gap-10 text-center">
            <div data-testid="stat-users">
              <div className="font-heading font-extrabold text-4xl sm:text-5xl text-coral">
                <AnimatedCounter value={stats.users_protected} suffix="+" testId="stat-users-count" />
              </div>
              <p className="mt-2 text-white/60 text-sm uppercase tracking-wide">{t("home.stats.users_protected")}</p>
            </div>
            <div data-testid="stat-alerts">
              <div className="font-heading font-extrabold text-4xl sm:text-5xl text-emeraldgreen">
                <AnimatedCounter value={stats.alerts_sent} suffix="+" testId="stat-alerts-count" />
              </div>
              <p className="mt-2 text-white/60 text-sm uppercase tracking-wide">{t("home.stats.alerts_sent")}</p>
            </div>
            <div data-testid="stat-places">
              <div className="font-heading font-extrabold text-4xl sm:text-5xl text-white">
                <AnimatedCounter value={stats.safe_places} suffix="+" testId="stat-places-count" />
              </div>
              <p className="mt-2 text-white/60 text-sm uppercase tracking-wide">{t("home.stats.safe_places")}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-offwhite">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionReveal className="max-w-2xl mb-14">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-slate900">
              {t("use_cases.section_title")}
            </h2>
            <p className="mt-4 text-lg text-slate-600">{t("use_cases.section_subtitle")}</p>
          </SectionReveal>

          <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-5">
            {USE_CASE_SLUGS.map((slug, i) => {
              const item = useCaseItems[slug];
              const spanClasses = [
                "md:col-span-5 lg:col-span-7 md:row-span-2",
                "md:col-span-3 lg:col-span-5",
                "md:col-span-3 lg:col-span-5",
                "md:col-span-4 lg:col-span-6",
                "md:col-span-4 lg:col-span-6",
                "md:col-span-8 lg:col-span-12",
              ];
              return (
                <SectionReveal key={slug} delay={i * 0.05} className={spanClasses[i]}>
                  <Link
                    to={`/${lang}/use-cases/${slug}`}
                    data-testid={`usecase-card-${slug}`}
                    className="group relative block rounded-3xl overflow-hidden h-full min-h-[220px] shadow-[0_8px_30px_rgba(30,58,138,0.1)]"
                  >
                    <img src={USE_CASE_IMAGES[slug]} alt={item.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate900/90 via-slate900/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 p-6">
                      <h3 className="font-heading font-bold text-xl text-white mb-1">{item.title}</h3>
                      <p className="text-white/70 text-sm hidden sm:block">{item.subtitle}</p>
                    </div>
                  </Link>
                </SectionReveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 text-center">
          <SectionReveal>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate900 max-w-2xl mx-auto">
              {t("home.final_cta.title")}
            </h2>
            <p className="mt-4 text-lg text-slate-600 max-w-xl mx-auto">{t("home.final_cta.subtitle")}</p>
            <DownloadButton testId="final-cta-button" className="mt-8 inline-flex items-center gap-2 bg-coral text-white font-semibold px-8 py-4 rounded-full hover:scale-[1.03] transition-transform duration-300">
              {t("home.final_cta.cta")} <ArrowRight size={18} />
            </DownloadButton>
          </SectionReveal>
        </div>
      </section>
    </div>
  );
}
